/**
 * Home — the premium long-scroll landing page composing the marketing sections in the
 * deck's register: hero, the promise, how-it-works proof chain, a Served-wall preview,
 * live impact stats, the impact map, testimonials, the donation money-flow and the FAQ.
 */
import { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ShieldCheck, MapPin, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button.jsx';
import { useReveal } from '../hooks/useReveal.js';
import { TrustStrip } from '../sections/TrustStrip.jsx';
import { Programs } from '../sections/Programs.jsx';
import { ApproachBand } from '../sections/ApproachBand.jsx';
import { ServedPreview } from '../sections/ServedPreview.jsx';
import { StatsBand } from '../sections/StatsBand.jsx';
import { Testimonials } from '../sections/Testimonials.jsx';
import { GetInvolved } from '../sections/GetInvolved.jsx';
import { DonationFlow } from '../sections/DonationFlow.jsx';
import { FAQ } from '../sections/FAQ.jsx';
import { CtaBand } from '../sections/CtaBand.jsx';
import { img, MEDIA } from '../data/media.js';
import styles from './Home.module.css';

// Leaflet is heavy and browser-only — load it lazily below the fold.
const ImpactMap = lazy(() => import('../sections/ImpactMap.jsx').then((m) => ({ default: m.ImpactMap })));

const STEPS = [
  { icon: ShieldCheck, n: '01', title: 'Verified intake', body: 'Staff record every case offline with documents — no public form, no flood vector.' },
  { icon: MapPin, n: '02', title: 'Ground verification', body: 'A field agent captures GPS-tagged before/after photos with timestamps baked in.' },
  { icon: CheckCircle2, n: '03', title: 'Delivered & published', body: 'On delivery, the beneficiary acknowledges and the case is published to the wall.' },
];

export default function Home() {
  const promiseRef = useReveal();
  const stepsRef = useReveal();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NGO',
    name: 'USMEHAR CARE',
    description: 'A welfare platform built around a public, auditable wall of proven, ground-verified help.',
    areaServed: 'Faridkot, Punjab, India',
  };

  return (
    <>
      <Helmet>
        <title>USMEHAR CARE — A wall of proven help</title>
        <meta name="description" content="A welfare platform built around a public, auditable Served wall of real, dated, ground-verified help. Every rupee accounted for." />
        <meta property="og:title" content="USMEHAR CARE — A wall of proven help" />
        <meta property="og:description" content="Every case verified on the ground. Every rupee accounted for. Verify the work yourself." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section
        className={styles.hero}
        style={{ '--hero-img': `url(${img(MEDIA.heroCommunity, 1600)})` }}
      >
        <div className={styles.heroGlow} aria-hidden="true" />
        <div className={styles.heroImage} aria-hidden="true" />
        <div className={`container ${styles.heroInner}`}>
          <p className={styles.heroEyebrow}>Impact &amp; Transparency Platform</p>
          <span className={styles.rule} aria-hidden="true" />
          <h1 className={styles.heroTitle}>
            Building a wall of <em>proven</em> help.
          </h1>
          <p className={styles.heroLede}>
            A welfare platform designed to outlast an election, defend every rupee, and let any
            sceptic verify the work for themselves.
          </p>
          <div className={styles.heroActions}>
            <Button to="/served" variant="gold" size="lg" iconRight={ArrowRight}>
              See the work
            </Button>
            <Button to="/donate" variant="onDark" size="lg">
              Donate
            </Button>
          </div>
          <div className={styles.heroStats}>
            <Stat value="Oct 2026" label="Live and serving cases" />
            <Stat value="3 surfaces" label="Public · Admin · Field" />
            <Stat value="100%" label="Audited, dated, verifiable" />
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* The promise */}
      <section className="container">
        <blockquote ref={promiseRef} className={styles.promise} data-revealed="false">
          The strongest thing this NGO can have going into the election is not a promise. It is a
          wall of real, proven, dated help that anyone can verify — and an audit trail behind it
          that cannot be argued with.
        </blockquote>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="container">
        <p className="eyebrow">How it works</p>
        <h2 className={styles.sectionTitle}>A proof chain behind every case.</h2>
        <div ref={stepsRef} className={styles.steps} data-revealed="false">
          {STEPS.map((s, i) => (
            <article key={s.n} className={styles.step} style={{ '--i': i }}>
              <span className={styles.stepNum}>{s.n}</span>
              <s.icon className={styles.stepIcon} size={26} aria-hidden="true" />
              <h3 className={styles.stepTitle}>{s.title}</h3>
              <p className={styles.stepBody}>{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      <Programs />
      <ServedPreview />
      <StatsBand />
      <ApproachBand />
      <Suspense fallback={<div style={{ height: 520 }} />}>
        <ImpactMap />
      </Suspense>
      <Testimonials />
      <GetInvolved />
      <DonationFlow />
      <FAQ />
      <CtaBand />
    </>
  );
}

function Stat({ value, label }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}
